/**
 * Operations safe to cache at the CDN edge.
 * Keys are operation names. Values are either a plain TTL number, an object
 * { ttl } for future plain-object entries, or { ttl, allowedPiiFields,
 * auditNote } where allowedPiiFields opts the operation out of the PII guard
 * for the named field tokens only.
 *
 * The per-op allowlist is a narrow, code-reviewed exemption. Adding a
 * field name here means: (a) the field carries no actual PII despite the
 * name (e.g., a public-policy Mux HLS URL), (b) Security has signed off,
 * (c) the underlying assumption (e.g., Mux playback_policy: "public")
 * is documented in the file referenced by auditNote.
 *
 * If the assumption changes (e.g., Mux moves to signed URLs), the
 * allowlist entry MUST be removed before the change ships.
 *
 * Adding a new operation requires:
 * 1. Verifying the resolver has no @Authorized() decorator
 * 2. Verifying the resolver does not access ctx.user
 * 3. Verifying the return type contains no PII fields, OR adding an
 *    audited entry to allowedPiiFields with a Security sign-off cited
 *    in auditNote (TypeScript enforces this link)
 *
 * REMOVED (Plan 055 / T-011):
 * - getChannel: epoch-keyed; CDN edge cannot key on epoch
 * - BrandTheme: epoch-keyed; CDN edge cannot key on epoch
 * - ThemeForSimulfest: epoch-keyed; CDN edge cannot key on epoch
 * - GetChannelWebThreeTokens: epoch-keyed; CDN edge cannot key on epoch
 * - ChatRoomsForChannel: embeds latest chat message; delete/edit/pin must be
 *   visible on immediate refetch, not after CloudFront TTL expiry.
 * - GetQuizzesForChannel: carries per-user selected_option_id after T-002
 * - GetRafflesForChannel: carries per-user has_joined after T-002
 */
export type CacheableOperationConfig =
  | number
  | {
      readonly ttl: number;
      readonly allowedPiiFields?: never;
      readonly auditNote?: never;
    }
  | {
      readonly ttl: number;
      readonly allowedPiiFields: readonly string[];
      readonly auditNote: string;
    };

export const CACHEABLE_OPERATIONS: Readonly<
  Record<string, CacheableOperationConfig>
> = {
  // 600s — static show fields (title, description, thumbnail, channel, theme, authorization rules, web3 token list, video list).
  // hls_url is allowlisted because Mux is configured with playback_policy: "public" (apps/api/src/services/mux/mux.client.ts:38).
  // auditNote points at the security sign-off document for this exemption.
  GetShowStatic: {
    ttl: 600,
    allowedPiiFields: ["hls_url", "hlsUrl"],
    auditNote: "plans/057-cdn-cache-restore/SECURITY-SIGNOFF-001.md",
  },
  GetPointsForWinningQuiz: 60, // 60s — rarely changes
  GetPointsForWinningRaffle: 60, // 60s — rarely changes
} as const;

export type CacheableOperation = keyof typeof CACHEABLE_OPERATIONS;

export interface CacheableEntry {
  readonly ttl: number;
  readonly allowedPiiFields: ReadonlySet<string>;
  readonly auditNote?: string;
}

const EMPTY_ALLOWLIST: ReadonlySet<string> = new Set();

// Pre-computed at module load — avoids per-call Set allocation on the hot path.
const COMPUTED_ENTRIES: ReadonlyMap<string, CacheableEntry> = new Map(
  Object.entries(CACHEABLE_OPERATIONS).map(([name, config]) => {
    if (typeof config === "number") {
      return [name, { ttl: config, allowedPiiFields: EMPTY_ALLOWLIST }];
    }
    if ("allowedPiiFields" in config) {
      return [
        name,
        {
          ttl: config.ttl,
          allowedPiiFields: new Set(config.allowedPiiFields),
          auditNote: config.auditNote,
        },
      ];
    }
    return [name, { ttl: config.ttl, allowedPiiFields: EMPTY_ALLOWLIST }];
  }),
);

/**
 * Return the normalized cache entry for an operation, or null if the
 * operation is not in the cacheable whitelist.
 *
 * Always returns a CacheableEntry with a ReadonlySet for allowedPiiFields
 * (empty set when no allowlist is configured). Callers never need to branch
 * on undefined — a null return means "not cacheable."
 */
export function getCacheableEntry(opName: string): CacheableEntry | null {
  return COMPUTED_ENTRIES.get(opName) ?? null;
}
